import { useCallback } from "react";

import { useAppDispatch } from "@/redux/hooks";

import { SelectType } from "@/enum/list.enum";

import { User } from "@/pages/Public/pages/Login/schema/login";
import { pickDefaultMailbox } from "@/pages/Private/pages/List/components/CampaignOverview/utils/resolveMyDefaultSender.util";

import {
	useGenerateBatchCopywritingMutation,
	useGenerateCopywritingMutation,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import { setGenerationQueued } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import {
	CopywritingChannel,
	CopywritingOutputFormat,
	GenerateBatchCopywritingRequest,
	GenerateBatchCopywritingResponse,
} from "./types";

/**
 * A bulk table selection to generate full sequences for — the footer "Reach out → Let AI write a
 * sequence" action. `CURRENT_PAGE` sends the explicit `selectedItems`; `ALL` sends the filter/status
 * so the backend resolves every matching lead across pages. `count` drives the credit display.
 */
export interface GenerateBarBulkSelection {
	listId: number;
	selectType: string;
	selectedItems: number[];
	count: number;
	filter?: Record<string, unknown>;
	inclusionFilterStatus?: string[];
	priorityFilterStatus?: string[];
	signalFilterStatus?: string[];
	assignStatus?: string;
	/** The table's search: a select-all made while searching covers the matches only (PREC-4443). */
	searchValue?: string;
	/** Every status of the tab the table showed; the Assigned tab stands for several (PREC-4450). */
	assignStatuses?: string[];
	/** The reason sub-filters the table showed, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons?: string[];
}

interface UseGenerateBarSubmitParams {
	prospectId: number;
	listId: number;
	channel: CopywritingChannel;
	language: string;
	batchMode: boolean;
	batchSize: number;
	upcomingLeadIds: number[];
	senderMode: "randomized" | "specific";
	selectedSendingUser: User | null;
	availableUsers: User[];
	userInstruction: string;
	linkedinInviteNote: boolean;
	frameworkOverrides: GenerateBatchCopywritingRequest["frameworkOverrides"];
	freshStartOverride: boolean;
	addInstruction: (instruction: string) => void;
	/** Present only for the bulk footer reach-out — switches Generate to batch-over-selection. */
	bulkSelection?: GenerateBarBulkSelection;
	/** Fired after a bulk batch is enqueued (single-lead / next-N paths use the socket instead). */
	onBulkGenerated?: (result: GenerateBatchCopywritingResponse) => void;
	/** Tracker signal seeding a single-lead generation (feed per-signal CTA, PREC-3635 P6). */
	seedSignalId?: number;
	/** Generate the single lead without any signal trigger (user removed the signal chip, PREC-3710). */
	ignoreSignal?: boolean;
}

/**
 * Owns GenerateBar's "Generate" action across its three modes — single lead, the "next N leads"
 * batch toggle, and (new) a bulk table selection. Extracted from GenerateBar so the component stays
 * under the file-length cap and the submit logic is independently testable.
 */
export const useGenerateBarSubmit = ({
	prospectId,
	listId,
	channel,
	language,
	batchMode,
	batchSize,
	upcomingLeadIds,
	senderMode,
	selectedSendingUser,
	availableUsers,
	userInstruction,
	linkedinInviteNote,
	frameworkOverrides,
	freshStartOverride,
	addInstruction,
	bulkSelection,
	onBulkGenerated,
	seedSignalId,
	ignoreSignal,
}: UseGenerateBarSubmitParams): { handleGenerate: () => Promise<void>; creditCount: number } => {
	const dispatch = useAppDispatch();

	const [generateCopywriting] = useGenerateCopywritingMutation();
	const [generateBatchCopywriting] = useGenerateBatchCopywritingMutation();

	// One credit per lead: the whole selection in bulk, the batch size for "next N leads", else one.
	const creditCount = bulkSelection ? bulkSelection.count : batchMode ? batchSize : 1;

	// Senders for a batch: all available (randomized) or the chosen one (the backend picks per lead).
	const batchSenderUserIds = () =>
		senderMode === "randomized"
			? availableUsers.map((u) => u.id)
			: selectedSendingUser
				? [selectedSendingUser.id]
				: availableUsers.map((u) => u.id);

	const handleGenerate = useCallback(async () => {
		// Record the instruction for autocomplete on the next generation (frontend only).
		addInstruction(userInstruction);

		// Bulk footer selection: generate a full sequence for every selected/matching lead.
		if (bulkSelection) {
			const isAll = bulkSelection.selectType === SelectType.ALL;

			try {
				const result = await generateBatchCopywriting({
					listId: bulkSelection.listId,
					listPersonIds: isAll ? undefined : bulkSelection.selectedItems,
					selectType: bulkSelection.selectType,
					filter: bulkSelection.filter,
					inclusionFilterStatus: bulkSelection.inclusionFilterStatus,
					priorityFilterStatus: bulkSelection.priorityFilterStatus,
					signalFilterStatus: bulkSelection.signalFilterStatus,
					assignStatus: bulkSelection.assignStatus,
					searchValue: bulkSelection.searchValue,
					assignStatuses: bulkSelection.assignStatuses,
					exclusionReasons: bulkSelection.exclusionReasons,
					channel,
					senderUserIds: batchSenderUserIds(),
					language: language || undefined,
					userInstruction: userInstruction || undefined,
					linkedinInviteNote,
					outputFormat: CopywritingOutputFormat.FULL_SEQUENCE,
					frameworkOverrides,
				}).unwrap();

				onBulkGenerated?.(result);
			} catch {
				// handled by slice extraReducers / the caller's snackbar
			}

			return;
		}

		if (batchMode) {
			const selectedLeadIds = upcomingLeadIds.slice(0, batchSize);

			if (selectedLeadIds.length === 0) {
				return;
			}

			try {
				const result = await generateBatchCopywriting({
					listId,
					listPersonIds: selectedLeadIds,
					channel,
					senderUserIds: batchSenderUserIds(),
					language: language || undefined,
					userInstruction: userInstruction || undefined,
					linkedinInviteNote,
					outputFormat: CopywritingOutputFormat.FULL_SEQUENCE,
					frameworkOverrides,
				}).unwrap();
				const currentEntry = result.accepted.find((a) => a.prospectId === prospectId);

				if (currentEntry) {
					dispatch(
						setGenerationQueued({
							prospectId: currentEntry.prospectId,
							listPersonId: currentEntry.listPersonId,
							eventId: currentEntry.eventId,
							receivedAt: Date.now(),
						})
					);
				}
			} catch {
				// handled by slice extraReducers
			}

			return;
		}

		try {
			await generateCopywriting({
				prospectId,
				listId,
				channel,
				language,
				senderUserId: selectedSendingUser?.id ?? 0,
				senderEmailId: pickDefaultMailbox(selectedSendingUser?.emails)?.id,
				userInstruction: userInstruction || undefined,
				linkedinInviteNote,
				outputFormat: CopywritingOutputFormat.FULL_SEQUENCE,
				frameworkOverrides,
				freshStartOverride: freshStartOverride || undefined,
				seedSignalId,
				ignoreSignal: ignoreSignal || undefined,
			}).unwrap();
		} catch {
			// handled by slice extraReducers
		}
	}, [
		batchMode,
		upcomingLeadIds,
		batchSize,
		listId,
		channel,
		language,
		senderMode,
		selectedSendingUser,
		availableUsers,
		userInstruction,
		linkedinInviteNote,
		prospectId,
		bulkSelection,
		onBulkGenerated,
		generateBatchCopywriting,
		generateCopywriting,
		freshStartOverride,
		frameworkOverrides,
		dispatch,
		addInstruction,
		seedSignalId,
		ignoreSignal,
	]);

	return { handleGenerate, creditCount };
};
